import React from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import type { PasswordStrength } from 'types/business'
import type { HttpErrorType } from 'types/errors'
import type { LoginErrorCode } from 'types/user'

import WeakPassword from 'components/password/weak-password-error'
import ContactUsLink from 'components/ui/contact-us-link'
import styles from './index.module.css'

type Error = {
  message?: string
  code: LoginErrorCode
  body?:
    | Array<string>
    | {
        detail: string
      }
    | string
}

// Custom hook to check if React Router is available
const useReactRouterAvailable = () => {
  try {
    // Attempt to use a React Router hook
    useNavigate()
    // If no error is thrown, React Router is available
    return true
  } catch (error) {
    // If an error is thrown, React Router is not available
    return false
  }
}

const BrowserLink = ({ to, children }) => (
  <a href={to} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)

function explainServerLoginError(
  errorOrigin: HttpErrorType | null | undefined,
  passwordStrength: PasswordStrength,
  redirectIfUrlExpired?: string,
  errorContext?: 'link',
  hasRouter?: boolean | null | undefined,
) {
  // TODO: refactor explainServerLoginError - too many arguments
  const error: Partial<HttpErrorType> = { ...errorOrigin }
  error.code = error.code?.replace(/(\['|'\])/g, '')
  // TODO: replace hasRouter with <BrowserRouter> from react-router v6
  const Link = hasRouter === false ? BrowserLink : RouterLink
  let message: React.ReactNode = null
  let hasWorkaround = false

  if (error.message) {
    message = error.message // here it spews a bit overdetailed messages which are nice for dev/qa
    // and, in general, should not make it to end-user
    //
    // e.g.
    //
    //     [404] (cannot POST https://example.disco.ac/api/misspelled-url-or-server-down/ (404)): Empty response
    //
  }

  if (
    error.body &&
    typeof error.body === 'object' &&
    !Array.isArray(error.body) &&
    'detail' in error.body
  ) {
    message = error.body.detail
  }

  if (error.code) {
    if (error.code === 'OLD_PASSWORD_INCORRECT') {
      message = 'The current password is not correct.'
    }

    if (error.code === 'ADD_TO_DISCO_ERROR_NO_DISCOS') {
      message = 'Could not find any DISCOs for this email'
    }

    if (error.code === 'ADD_TO_DISCO_ERROR_ALREADY_ADDED') {
      message = (
        <span className={styles.loginErrorConfirm}>
          Good news! This playlist is already in your DISCO.
        </span>
      )
    }

    if (
      error.code === 'PASSWORD_INCORRECT' ||
      error.code === 'ADD_TO_DISCO_ERROR_UNAUTHORIZED'
    ) {
      message = <span>{error.message}</span>
    }

    if (error.code === 'breached_password') {
      message = (
        <span>
          Sorry, that password is not strong enough, and may have been used on
          other systems. You must{' '}
          <Link to="/forgot-password">reset your password</Link> with a new,
          stronger password before you can log in.{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://support.disco.ac/home/q-how-do-i-choose-a-secure-password"
          >
            Find out more.
          </a>
        </span>
      )
    }

    if (error.code === 'LOGIN_LIMIT_EXCEEDED') {
      message = (
        <div className={styles.loginErrorLimitExceeded}>
          <span>
            Your account is locked out due to too many invalid login attempts.{' '}
            <br />
            <br />
            Please reset your password or{' '}
            <ContactUsLink>contact support</ContactUsLink> to regain access.
            <br />
            <br />
          </span>
        </div>
      )
    }

    if (
      error.code === 'NEW_PASSWORD_TOO_WEAK' &&
      error.body &&
      typeof error.body === 'object' &&
      !Array.isArray(error.body) &&
      'detail' in error.body
    ) {
      message = (
        <WeakPassword
          passwordStrength={error.body.detail as PasswordStrength}
        />
      )
    }
  }

  let serverMessage = ''

  if (typeof error.body === 'string') {
    serverMessage = error.body
  } else if (Array.isArray(error.body) && typeof error.body[0] === 'string') {
    serverMessage = error.body[0]
  }

  if (serverMessage) {
    if (serverMessage === 'invalid referer URL') {
      message = 'Sorry! Link is invalid.'
    } else if (serverMessage === 'invite has been deleted') {
      message = 'Sorry! This link is no longer valid.'
    } else if (serverMessage === 'referer already used') {
      message = 'Sorry, this link has been already been used.'
    } else if (serverMessage === 'password is too weak') {
      message = <WeakPassword passwordStrength={passwordStrength} />
    } else if (
      serverMessage === 'referer URL has expired' &&
      redirectIfUrlExpired
    ) {
      hasWorkaround = true
      message = (
        <span>
          Sorry, the link has expired.{' '}
          <Link to="/forgot-password">Request a new link.</Link>
        </span>
      )
    } else if (errorContext === 'link') {
      //handles "referer URL has expired"
      message = `Sorry! Link is invalid: ${serverMessage}.`
    } else {
      message = `Error: ${serverMessage}.`
    }
  }

  return {
    message,
    hasWorkaround,
  }
}

type Props = {
  suggestContactingSupport?: boolean
  redirectIfUrlExpired?: string
  errorContext?: 'link'
  error: HttpErrorType | null | undefined
  passwordStrength: PasswordStrength
}

const LoginError = ({
  suggestContactingSupport,
  redirectIfUrlExpired,
  errorContext,
  error,
  passwordStrength,
}: Props): React.ReactElement<'div'> => {
  const hasRouter = useReactRouterAvailable()

  const _renderContactSupport = (hasWorkaround: boolean) => {
    if (!suggestContactingSupport) return
    const support = (
      <a
        href="mailto:support@disco.ac"
        target="_blank"
        rel="noopener noreferrer"
      >
        support@disco.ac
      </a>
    )

    if (hasWorkaround) {
      return <div>Still having trouble? Contact {support}.</div>
    } else {
      return (
        <div>
          If you think you’re receiving this message in error, please contact{' '}
          {support}.
        </div>
      )
    }
  }

  const { message, hasWorkaround } = explainServerLoginError(
    error,
    passwordStrength,
    redirectIfUrlExpired,
    errorContext,
    hasRouter,
  )
  return (
    <div className={styles.loginErrorContainer}>
      {message}
      {_renderContactSupport(hasWorkaround)}
    </div>
  )
}

export default LoginError
